/*
  File: helpTopics.tsx
  Path: src/components/helpTopics.tsx
  Description: The whole Help corpus for the Pro dashboard — a hardcoded list of topics.
    Viking-minimum: no CMS, no backend, no search index. "Search" is just filtering
    this array. To add or change help, edit an entry here. Each topic knows which
    routes it belongs to (match[]) so the drawer can tag the page you're on.
  Author: Larry McLean / Claude
  Created: 2026-08-11
  Version: 1.1.0
  Status: ACTIVE
  Change Log (append-only):
    - 2026-08-11 - v1.0.0 - Initial topic registry: Ops Board, Schedule, Calendar,
      Manifest, Dive Sites, Vehicles & Vessels, Walk-Ins, Customers (incl. Import),
      Bookings, Courses, Billing, Team, Settings. [Larry McLean / Claude]
    - 2026-08-11 - v1.1.0 - Terminology: "currency" -> "readiness" for the three
      staff clocks (Schedule + Team). Money "currency" (USD/EGP) untouched. [Larry McLean / Claude]
*/

import React from "react";

export type HelpTopic = {
  id: string;
  label: string;
  /** Route fragments this topic covers. First topic whose fragment the current
   *  path starts with is the "this page" topic. Order matters — longest/most
   *  specific fragments should not be shadowed by short ones. */
  match: string[];
  /** Lowercased words used only for search matching. */
  keywords: string;
  /** One-line teaser shown in the topic list. */
  teaser: string;
  /** Full card body shown when the topic is opened. */
  body: React.ReactNode;
};

export const HELP_TOPICS: HelpTopic[] = [
  /* ── Ops Board ─────────────────────────────────────────────── */
  {
    id: "ops-board",
    label: "Ops Board",
    match: ["/ops-board", "/opsboard"],
    keywords: "ops board tv display weather sunset today live public screen",
    teaser: "The live day-view for the shop TV.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Ops Board</h3>
        <p className="aqx-help-lead">
          A live, read-only view of <b>today</b> — trips, weather, and sunset — built to run
          full-screen on a TV in the shop. It refreshes on its own every few seconds; nobody has
          to touch it.
        </p>

        <h4 className="aqx-help-sub">Where the weather &amp; sunset come from</h4>
        <p className="aqx-help-note">
          Both are read from the <b>coordinates on your dive sites</b>. If the board shows no
          weather, it usually means a dive site is missing its latitude/longitude — open{" "}
          <b>Dive Sites</b> and add them.
        </p>

        <h4 className="aqx-help-sub">Putting it on the TV</h4>
        <p className="aqx-help-note">
          Open the public board link in the browser and press full-screen. The board is safe to
          leave on all day — it never shows customer or staff private data.
        </p>
      </>
    ),
  },

  /* ── Schedule ──────────────────────────────────────────────── */
  {
    id: "schedule",
    label: "Schedule",
    match: ["/schedule"],
    keywords: "schedule assign guide instructor staff roster trips assignment",
    teaser: "Assign guides and instructors to trips.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Schedule</h3>
        <p className="aqx-help-lead">
          Assign your guides and instructors to the day's trips and courses. Only staff who are{" "}
          <b>assignable</b> — meaning their professional readiness is in date — appear here.
        </p>
        <h4 className="aqx-help-sub">If someone's missing</h4>
        <p className="aqx-help-note">
          They're probably lapsed on one of the three readiness clocks. Open <b>Team</b>, find
          them, and check Membership, Insurance, and EFR/CPR. Once all three are current they
          become assignable again.
        </p>
      </>
    ),
  },

  /* ── Calendar ──────────────────────────────────────────────── */
  {
    id: "calendar",
    label: "Calendar",
    match: ["/calendar"],
    keywords: "calendar month week trips courses overview dates",
    teaser: "Month and week view of trips and courses.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Calendar</h3>
        <p className="aqx-help-lead">
          A month- and week-level view of everything on the books — trips, courses, and bookings —
          so you can see how busy a day or week is at a glance.
        </p>
        <p className="aqx-help-note">
          Use <b>Schedule</b> for the day-by-day work of assigning staff; use the Calendar to plan
          ahead and spot gaps.
        </p>
      </>
    ),
  },

  /* ── Manifest ──────────────────────────────────────────────── */
  {
    id: "manifest",
    label: "Manifest",
    match: ["/manifest"],
    keywords: "manifest roster who is diving check-in trip list guide gear boat",
    teaser: "The roster for a trip — who's diving and who's guiding.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Manifest</h3>
        <p className="aqx-help-lead">
          The roster for a trip: who's diving, which guide or instructor is running it, and what's
          on the boat. This is the sheet you work from on the day.
        </p>
        <h4 className="aqx-help-sub">How it fills up</h4>
        <p className="aqx-help-note">
          Divers arrive on the manifest from <b>Bookings</b> and <b>Walk-Ins</b>. The guide/
          instructor comes from what you set on <b>Schedule</b>.
        </p>
      </>
    ),
  },

  /* ── Dive Sites ────────────────────────────────────────────── */
  {
    id: "dive-sites",
    label: "Dive Sites",
    match: ["/dive-sites", "/sites", "/divesites"],
    keywords: "dive sites reef wreck depth coordinates latitude longitude location map",
    teaser: "Your sites — and the coordinates that drive weather.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Dive Sites</h3>
        <p className="aqx-help-lead">
          The sites your shop runs — name, depth, and location. Keep them tidy here and they show
          up everywhere else you plan a dive.
        </p>
        <h4 className="aqx-help-sub">Coordinates matter</h4>
        <p className="aqx-help-note">
          Each site's <b>latitude and longitude</b> feed the weather and sunset on the Ops Board.
          If a site has no coordinates, the board can't show conditions for it — add them here.
        </p>
      </>
    ),
  },

  /* ── Vehicles & Vessels ────────────────────────────────────── */
  {
    id: "vehicles-vessels",
    label: "Vehicles & Vessels",
    match: ["/vehicles", "/vessels", "/fleet"],
    keywords: "vehicles vessels boats trucks vans fleet capacity transport seats",
    teaser: "Your boats and vehicles, and how many they carry.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Vehicles &amp; Vessels</h3>
        <p className="aqx-help-lead">
          Your boats and vehicles — what you have and how many people each one carries. This is
          what the day's logistics get built around.
        </p>
        <p className="aqx-help-note">
          Capacity here helps keep a trip from being over-booked past what the boat or van can
          actually take.
        </p>
      </>
    ),
  },

  /* ── Walk-Ins ──────────────────────────────────────────────── */
  {
    id: "walk-ins",
    label: "Walk-Ins",
    match: ["/walk-ins", "/walkins"],
    keywords: "walk-ins walk in drop in quick add customer counter today",
    teaser: "Quickly capture a customer who walks in.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Walk-Ins</h3>
        <p className="aqx-help-lead">
          A fast way to capture someone who shows up at the counter without a booking — grab the
          essentials now, get them on today's dive, tidy the details later.
        </p>
        <p className="aqx-help-note">
          A walk-in becomes a real customer record, so you don't have to re-type them next time.
        </p>
      </>
    ),
  },

  /* ── Customers (incl. Import) ──────────────────────────────── */
  {
    id: "customers",
    label: "Customers",
    match: ["/customers"],
    keywords:
      "customers people divers import csv spreadsheet upload template add profile email country code",
    teaser: "Your divers — add one, or import a whole list.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Customers</h3>
        <p className="aqx-help-lead">
          Everyone who dives with you. Add them one at a time, or bring in a whole list from a
          spreadsheet.
        </p>

        <h4 className="aqx-help-sub">Import customers</h4>
        <p className="aqx-help-note" style={{ marginBottom: 14 }}>
          Adds customer profiles from a spreadsheet. This does <b>not</b> create bookings,
          sessions, forms, or payments — just the people.
        </p>

        <div className="aqx-help-steps">
          <div className="aqx-help-step">
            <span className="aqx-help-num">1</span>
            <span>Click <b>Download template</b> and open it.</span>
          </div>
          <div className="aqx-help-step">
            <span className="aqx-help-num">2</span>
            <span>
              Put one customer per row. Only <b>first name</b> and <b>last name</b> are required —
              blank optional fields are fine.
            </span>
          </div>
          <div className="aqx-help-step">
            <span className="aqx-help-num">3</span>
            <span>Save it as a CSV, come back here, and upload it.</span>
          </div>
        </div>

        <h4 className="aqx-help-sub">What goes in each column</h4>
        <table className="aqx-help-table">
          <thead>
            <tr>
              <th>Column</th>
              <th>Need it?</th>
              <th>What to type</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>first_name</td><td><b>Required</b></td><td>e.g. Phillip</td></tr>
            <tr><td>last_name</td><td><b>Required</b></td><td>e.g. Huggins</td></tr>
            <tr><td>preferred_name</td><td>Optional</td><td>the name they go by — e.g. Phil</td></tr>
            <tr><td>email</td><td>Recommended</td><td>phil@huggins.com</td></tr>
            <tr><td>phone</td><td>Optional</td><td>digits incl. country code — 15092763426</td></tr>
            <tr><td>date_of_birth</td><td>Optional</td><td>YYYY-MM-DD — 1987-06-20</td></tr>
            <tr><td>nationality_code</td><td>Optional</td><td>2-letter country code — US</td></tr>
            <tr><td>gender</td><td>Optional</td><td><code>male</code> or <code>female</code></td></tr>
            <tr><td>id_document_type</td><td>Optional</td><td><code>passport</code> or <code>national_id</code></td></tr>
            <tr><td>id_document_number</td><td>Optional</td><td>e.g. P1234567</td></tr>
            <tr><td>certifications</td><td>Optional</td><td>agency + level — SSI Open Water</td></tr>
            <tr><td>medical_readiness_status</td><td>Optional</td><td>advanced — leave blank unless recording a medical status</td></tr>
          </tbody>
        </table>

        <h4 className="aqx-help-sub">Country codes</h4>
        <p className="aqx-help-note">
          Use the standard 2-letter code: <b>US</b> (United States) · <b>EG</b> (Egypt) ·{" "}
          <b>JO</b> (Jordan) · <b>GB</b> (United Kingdom) · <b>DE</b> (Germany).
        </p>

        <h4 className="aqx-help-sub">If your upload is rejected</h4>
        <p className="aqx-help-note">
          It usually means the column headers don't match. Click <b>Download template</b>, paste
          your data under its headers, and upload again.
        </p>
      </>
    ),
  },

  /* ── Bookings ──────────────────────────────────────────────── */
  {
    id: "bookings",
    label: "Bookings",
    match: ["/bookings"],
    keywords: "bookings reservations trips deposit payment reserve seats",
    teaser: "Reservations for trips and courses.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Bookings</h3>
        <p className="aqx-help-lead">
          Reservations for your trips and courses — who's booked on what, and where they are in the
          process.
        </p>
        <p className="aqx-help-note">
          Booked divers flow onto the <b>Manifest</b> for the day they're diving.
        </p>
      </>
    ),
  },

  /* ── Courses ───────────────────────────────────────────────── */
  {
    id: "courses",
    label: "Courses",
    match: ["/courses"],
    keywords: "courses training certification open water advanced student enroll",
    teaser: "The courses you run and who's enrolled.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Courses</h3>
        <p className="aqx-help-lead">
          The training you offer — from a first Try Scuba through certifications — and the students
          working through them.
        </p>
        <p className="aqx-help-note">
          Course sessions are assigned to instructors on <b>Schedule</b>, the same as trips.
        </p>
      </>
    ),
  },

  /* ── Billing ───────────────────────────────────────────────── */
  {
    id: "billing",
    label: "Billing",
    match: ["/billing", "/invoices"],
    keywords: "billing invoice payment tax rate invoice prefix receipt money charge",
    teaser: "Invoices, payments, and your tax settings.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Billing</h3>
        <p className="aqx-help-lead">
          Invoices and payments for your shop, along with the tax rate and invoice numbering that
          appear on what your customers receive.
        </p>
        <p className="aqx-help-note">
          Set your tax rate and invoice prefix once and they carry through to every new invoice.
        </p>
      </>
    ),
  },

  /* ── Team ──────────────────────────────────────────────────── */
  {
    id: "team",
    label: "Team",
    match: ["/team", "/staff"],
    keywords:
      "team staff guide instructor readiness currency assignable membership insurance efr cpr add member work permit residency",
    teaser: "Your dive staff and whether they're ready to work.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Team</h3>
        <p className="aqx-help-lead">
          Your dive staff — who works here, what they're qualified to run, and whether their
          professional readiness is in date. This roster feeds guide and instructor assignment on{" "}
          <b>Schedule</b>.
        </p>

        <h4 className="aqx-help-sub">The three clocks</h4>
        <p className="aqx-help-note" style={{ marginBottom: 12 }}>
          A staff member is <b>Assignable</b> only when all three of these are current:
        </p>
        <div className="aqx-help-steps">
          <div className="aqx-help-step">
            <span className="aqx-help-num">1</span>
            <span><b>Membership / teaching status</b> — their agency membership is in date.</span>
          </div>
          <div className="aqx-help-step">
            <span className="aqx-help-num">2</span>
            <span><b>Professional liability insurance</b> — an individual policy, required to guide or teach in this region.</span>
          </div>
          <div className="aqx-help-step">
            <span className="aqx-help-num">3</span>
            <span><b>EFR / CPR readiness</b> — their first-aid qualification is in date.</span>
          </div>
        </div>

        <h4 className="aqx-help-sub">The counters up top</h4>
        <p className="aqx-help-note">
          <b>Assignable Now</b> can work today · <b>Total Staff</b> everyone on the roster ·{" "}
          <b>Expiring &lt; 30 days</b> renew these soon · <b>Lapsed · Not assignable</b> at least
          one clock has run out.
        </p>

        <h4 className="aqx-help-sub">Adding someone</h4>
        <p className="aqx-help-note">
          Click <b>+ Add team member</b>. Only <b>Full name</b> is required to save; fill in role
          (Guide or Instructor), agency, certification level, pro number, and the three clocks so
          they can be assigned.
        </p>

        <h4 className="aqx-help-sub">Work eligibility (record only)</h4>
        <p className="aqx-help-note">
          Residency, ID document, and work-permit fields are for your records only. They're masked,
          never shown on the roster, and <b>do not affect whether someone is assignable</b>.
        </p>
      </>
    ),
  },

  /* ── Settings ──────────────────────────────────────────────── */
  {
    id: "settings",
    label: "Settings",
    match: ["/settings"],
    keywords: "settings preferences shop configuration options branding",
    teaser: "Shop settings — more help coming here soon.",
    body: (
      <>
        <h3 className="aqx-help-card-title">Settings</h3>
        <p className="aqx-help-lead">
          Where your shop's preferences live. We're building this section out — more detailed help
          will land here as the settings themselves are finished.
        </p>
        <p className="aqx-help-note">
          Stuck on a specific setting in the meantime? Contact the AQUORIX team and we'll walk you
          through it.
        </p>
      </>
    ),
  },
];

/** Return the topic that owns the given route path, or null if none. */
export function topicForPath(pathname: string): HelpTopic | null {
  if (!pathname) return null;
  const path = pathname.toLowerCase();
  for (const topic of HELP_TOPICS) {
    if (topic.match.some((frag) => path.startsWith(frag) || path.includes(frag))) {
      return topic;
    }
  }
  return null;
}
